import { useMemo } from 'react';

import type { UseDocumentSelectionReturn } from '../hooks/useDocumentSelection';
import type { UseTemplateGenerationReturn } from '../hooks/useTemplateGeneration';
import type {
  OnboardingDocumentState,
  OnboardingTemplateState,
} from './OnboardingContext';

export function useTemplateState(
  templateHook: UseTemplateGenerationReturn,
  displayName: string,
  displayContent: string,
  createdTemplateName: string | null
): OnboardingTemplateState {
  return useMemo(
    () => ({
      generatedTemplateDisplayName: displayName,
      generatedTemplateDisplayContent: displayContent,
      createdTemplateName: createdTemplateName ?? undefined,
      isEditingTemplate: templateHook.isEditingTemplate,
      setIsEditingTemplate: templateHook.setIsEditingTemplate,
      isGenerating: templateHook.isGenerating,
      isEditing: templateHook.isEditing,
      handleCreateTemplate: templateHook.handleCreateTemplate,
      handleSkipTemplate: templateHook.handleSkipTemplate,
      handleSaveAndContinue: templateHook.handleSaveAndContinue,
      handleApplyChanges: templateHook.handleApplyChanges,
      handleUndoLastTemplateEdit: templateHook.handleUndoLastTemplateEdit,
      canUndoLastTemplateEdit: templateHook.canUndoLastTemplateEdit,
      handleTemplateNameChange: templateHook.handleTemplateNameChange,
      handleTemplateContentChange: templateHook.handleTemplateContentChange,
    }),
    [
      displayName,
      displayContent,
      createdTemplateName,
      templateHook.isEditingTemplate,
      templateHook.setIsEditingTemplate,
      templateHook.isGenerating,
      templateHook.isEditing,
      templateHook.handleCreateTemplate,
      templateHook.handleSkipTemplate,
      templateHook.handleSaveAndContinue,
      templateHook.handleApplyChanges,
      templateHook.handleUndoLastTemplateEdit,
      templateHook.canUndoLastTemplateEdit,
      templateHook.handleTemplateNameChange,
      templateHook.handleTemplateContentChange,
    ]
  );
}

export function useDocumentState(
  docHook: UseDocumentSelectionReturn
): OnboardingDocumentState {
  return useMemo(
    () => ({
      documentTemplates: docHook.templates,
      selectedDocumentIds: docHook.selectedIds,
      selectedTemplates: docHook.selectedTemplates,
      isLoadingDocuments: docHook.isLoading,
      hasDocumentSelectionError: docHook.hasError,
      toggleDocumentSelection: docHook.toggleSelection,
      submitFavorites: docHook.submitFavorites,
    }),
    [
      docHook.templates,
      docHook.selectedIds,
      docHook.selectedTemplates,
      docHook.isLoading,
      docHook.hasError,
      docHook.toggleSelection,
      docHook.submitFavorites,
    ]
  );
}
